import {ReviewDetailsFragment} from '../../schema-types/ReviewDetailsFragment';
import {ActionType, FormField} from '../../components/AutoForm/AutoFieldNew';
import {AccountStatuses_accountDetails} from '../../schema-types/AccountStatuses';
import {AccountBasicFragment} from '../../schema-types/AccountBasicFragment';
import {
  communicationMethodOptions,
  numbersToPhrasing,
} from '../PrecursorWizard/fields/reviewAction';
import {useRTQ} from '../../hooks/useRTQ';

export enum ReviewReason {
  adHoc = 'Change of client circumstances',
  annual = 'Ongoing client service',
  initial = 'Initial client meeting',
}

export enum ContactAttribution {
  both = 'both',
}

export const getContactAttribution = (
  accountDetails: AccountBasicFragment,
  review: ReviewDetailsFragment
) => {
  if (!review?.contact) {
    if (accountDetails.primaryContact && accountDetails.secondaryContact)
      return ContactAttribution.both;
  }
  return review?.contact?.id;
};

export const getUncontactableFields = (
  review: ReviewDetailsFragment | undefined
): FormField[] => {
  const fields = review?.clientCommunicationAttempts?.map<FormField[]>(
    (attempt, index) => {
      const attemptFields: FormField[] = [
        {
          id: `previousCommunications.${index}.contactMethod`,
          label: `${numbersToPhrasing[index]} attempt method`,
          type: ActionType.select,
          options: communicationMethodOptions,
          fullWidth: true,
        },
        {
          id: `previousCommunications.${index}.contactDate`,
          label: `${numbersToPhrasing[index]} attempt date`,
          type: ActionType.datepicker,
          fullWidth: true,
        },
      ];
      if (attempt.declineReason) {
        attemptFields.push({
          id: `previousCommunications.${index}.declineReason`,
          label: `${numbersToPhrasing[index]} decline comment`,
          type: ActionType.text,
          multiline: true,
          fullWidth: true,
        });
      }
      return attemptFields;
    }
  );
  return fields?.flat() || [];
};

export const useGetHeaderFields = (
  accountDetails: AccountBasicFragment | undefined,
  accountStatuses: AccountStatuses_accountDetails | null,
  review: ReviewDetailsFragment | undefined
): FormField[] => {
  const {atrName} = useRTQ();
  const reasonVal = [
    ReviewReason.annual,
    ReviewReason.adHoc,
    ReviewReason.initial,
  ].includes(review?.reviewReason as any)
    ? review?.reviewReason
    : 'Other';

  const fields: FormField[] = [
    {
      id: 'reviewReason',
      fullWidth: true,
      label: 'Reason',

      type: ActionType.text,
      initialValue: reasonVal || undefined,
    },
    {
      id: 'status',
      fullWidth: true,
      label: 'Status',
      type: ActionType.text,
      initialValue: undefined,
    },
    {
      id: 'name',
      fullWidth: true,
      label: 'Review name',
      type: ActionType.text,

      initialValue: review?.name?.replace(/undefined/gi, '') || '',
    },
    {
      id: 'reviewFocus',
      fullWidth: true,
      label: 'Focus',
      type: ActionType.text,
    },
    getSummaryReviewContactField(review, accountDetails),

    ...getUncontactableFields(review),
  ];

  if (review?.dateBooked) {
    fields.push({
      id: 'bookedMeetingDate',
      fullWidth: true,
      label: 'Booked for',
      type: ActionType.datepicker,
    });
  }
  if (review?.dateScheduled && !review?.dateBooked) {
    fields.push({
      id: 'plannedMeetingDate',
      fullWidth: true,
      label: 'Planned for',
      type: ActionType.datetimepicker,
    });
  }
  if (review?.meetingType) {
    fields.push({
      id: 'meetingType',
      fullWidth: true,
      label: 'Meeting',
      type: ActionType.select,
      options: [
        {
          label: 'Zoom',
          value: 'Web',
        },
        {
          label: 'Face to face',
          value: 'Face to face',
        },
        {
          label: 'Telephone',
          value: 'Telephone',
        },
        {
          label: 'Email',
          value: 'E-mail',
        },
      ],
    });
  }
  if (accountDetails?.primaryContact) {
    const label = `${accountDetails.primaryContact.simplifiedName}'s ${atrName} score`;
    fields.push({
      id: 'primaryAtrValue',
      fullWidth: true,
      label: accountStatuses?.primaryContact ? label : `${label} loading...`,
      type: ActionType.text,
    });
    if (accountStatuses?.primaryContact?.atr?.finalValue) {
      fields.push({
        id: 'primaryAtrDate',
        fullWidth: true,
        label: `${accountDetails.primaryContact.simplifiedName}'s ${atrName} Date`,
        type: ActionType.datepicker,
      });
    }
  }
  if (accountDetails?.secondaryContact) {
    const label = `${accountDetails.secondaryContact.simplifiedName}'s ${atrName} score`;
    fields.push({
      id: 'secondaryAtrValue',
      fullWidth: true,
      label: accountStatuses?.secondaryContact ? label : `${label} loading...`,
      type: ActionType.text,
    });
    if (accountStatuses?.secondaryContact?.atr?.finalValue) {
      fields.push({
        id: 'secondaryAtrDate',
        fullWidth: true,
        label: `${accountDetails.secondaryContact.simplifiedName}'s ${atrName} Date`,
        type: ActionType.datepicker,
      });
    }
  }
  fields.push({
    id: 'opportunityValue',
    fullWidth: true,
    label: 'Opportunity Value',
    type: ActionType.text,
    currency: true,
  });
  return fields;
};

export function getSummaryReviewContactField(
  review: ReviewDetailsFragment | undefined,
  accountDetails: AccountBasicFragment | undefined
): FormField {
  return {
    id: 'reviewContact',
    fullWidth: true,
    label: 'Contact',
    type: ActionType.select,
    options: [
      {
        value: accountDetails?.primaryContact?.id,
        label: accountDetails?.primaryContact?.simplifiedName as string,
      },
      ...(accountDetails?.secondaryContact
        ? [
            {
              value: accountDetails.secondaryContact.id as string,
              label: accountDetails.secondaryContact.simplifiedName as string,
            },
            {
              value: ContactAttribution.both,
              label: `${accountDetails?.primaryContact?.simplifiedName} and ${accountDetails.secondaryContact.simplifiedName}`,
            },
          ]
        : []),
    ],
  };
}
